import { currency } from '@/states';

const formatPerDay = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return {
    numeric: n,
    formatted: n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  };
};

const PRICE_FIELDS = [
  { key: 'from_price_per_day', label: '1 Day' },
  { key: 'from_price_per_3_days', label: '3 Days' },
  { key: 'from_price_per_week', label: '7 Days' },
];

export default function CarPriceCompact({ model, className = '' }) {
  const values = PRICE_FIELDS.map((f) => ({
    ...f,
    value: money(model?.[f.key]),
  }));
  const hasAny = values.some((v) => v.value != null);
  const byKey = Object.fromEntries(values.map((v) => [v.key, v.value]));
  const day1 = byKey.from_price_per_day?.numeric ?? null;
  const day3 = byKey.from_price_per_3_days?.numeric ?? null;
  const day7 = byKey.from_price_per_week?.numeric ?? null;
  // Keep /day consistent with the weekly price users actually see in the card.
  const day7Displayed = day7 != null ? Math.round(day7) : null;
  const hasComparable = day1 != null && day3 != null && day7 != null;
  const isBestValue = hasComparable && day7 / 7 <= Math.min(day1, day3 / 3);
  const weeklyPerDay = day7Displayed != null ? formatPerDay(day7Displayed / 7) : null;

  if (!hasAny) {
    return <span className="text-muted small">Price on request</span>;
  }

  return (
    <div className={`car-price-compact ${className}`.trim()}>
      <table className="car-price-compact__table" role="presentation" aria-label="Reference rental prices">
        <colgroup>
          <col className="car-price-compact__col car-price-compact__col--day-1" />
          <col className="car-price-compact__col car-price-compact__col--day-3" />
          <col className="car-price-compact__col car-price-compact__col--day-7" />
        </colgroup>
        <thead>
          <tr>
            {values.map((v) => (
              <th
                key={`h-${v.key}`}
                className={`car-price-compact__head-cell ${
                  v.key === 'from_price_per_week' ? 'car-price-compact__head-cell--week' : ''
                } ${
                  v.key === 'from_price_per_week' && isBestValue ? 'car-price-compact__head-cell--best' : ''
                }`}
                scope="col"
              >
                <span className="car-price-compact__head-content">
                  <span className="car-price-compact__head-label">{v.label}</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {values.map((v) => (
              <td
                key={`v-${v.key}`}
                className={`car-price-compact__value-cell ${
                  v.key === 'from_price_per_week' && isBestValue ? 'car-price-compact__value-cell--best' : ''
                }`}
              >
                {v.value != null ? (
                  <>
                    <span className="car-price-compact__value-main">{currency}{v.value.formatted}</span>
                    {v.key === 'from_price_per_week' && weeklyPerDay != null ? (
                      <span className="car-price-compact__value-sub">{currency}{weeklyPerDay}/day</span>
                    ) : null}
                  </>
                ) : (
                  '—'
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
